// @givmo/app-admin — Settings → Payment Methods decode/mapping (07-1.3).
//
// Pure, side-effect-free mapping from the processor-NEUTRAL `CardOut` wire model (the `/v2` card
// surface — `{ id, brand, last4, exp_month, exp_year, is_default }`) to the display shape the tiles
// render. The `/v2` neutral surface is the go-forward contract for new clients: `is_default` is
// server-computed and the Shift4-only passthrough fields are dropped, so the admin app never
// cross-references the wallet or parses processor-shaped JSON.
//
// Mirrors the legacy Flutter `payment_method_tile.dart` display: "{Brand} Card *{last4}" with a brand
// icon and an EXP line. Kept as its own module (no React) so the field mapping is unit-testable in
// isolation — matching the `dashboard-decode.ts` split.

import type { CardListOut, CardOut } from '@givmo/api-client';

/** A stored card reduced to exactly the fields the tiles render. */
export interface PaymentCard {
  /** The Shift4/processor card id — the delete path param. Cards without one are dropped. */
  id: string;
  /** Card network label (e.g. "Visa", "MasterCard"), or "Card" when the processor omits it. */
  brand: string;
  /** The last four PAN digits, or null when absent (the tile then shows just the brand). */
  last4: string | null;
  /** "MM / YY" expiry, or null when either component is missing. */
  expiry: string | null;
  /** Server-computed default flag (the wallet's selected card). */
  isDefault: boolean;
}

/**
 * Map one neutral `CardOut` to the display shape, or `null` when it has no `id` (a card we could
 * neither key nor delete — never rendered). `id` is the ONLY hard requirement; every other field
 * degrades gracefully.
 */
export function toPaymentCard(card: CardOut): PaymentCard | null {
  const id = typeof card.id === 'string' && card.id.length > 0 ? card.id : null;
  if (id === null) return null;
  return {
    id,
    brand: cardBrandLabel(card.brand),
    last4: typeof card.last4 === 'string' && card.last4.length > 0 ? card.last4 : null,
    expiry: formatExpiry(card.exp_month, card.exp_year),
    isDefault: card.is_default === true,
  };
}

/**
 * Extract the renderable cards from a `CardListOut` (`{ cards: [...] }`) envelope, dropping any card
 * without an `id`. Tolerant of a missing/undefined `cards` array (→ empty list).
 */
export function extractCards(list: CardListOut | undefined): PaymentCard[] {
  const raw = list?.cards ?? [];
  const out: PaymentCard[] = [];
  for (const card of raw) {
    const mapped = toPaymentCard(card);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

/** The card-network label, falling back to a generic "Card" when the processor omits the brand. */
export function cardBrandLabel(brand: string | null | undefined): string {
  return typeof brand === 'string' && brand.trim().length > 0 ? brand.trim() : 'Card';
}

/**
 * Format a card expiry as "MM / YY". Returns `null` unless BOTH components are present. The month is
 * left-padded to two digits; a 4-digit year is reduced to its last two (2027 → "27"). Non-numeric or
 * empty inputs yield `null` rather than a malformed string.
 */
export function formatExpiry(
  month: string | null | undefined,
  year: string | null | undefined,
): string | null {
  const mm = normalizeMonth(month);
  const yy = normalizeYear(year);
  if (mm === null || yy === null) return null;
  return `${mm} / ${yy}`;
}

/** A card tile's headline text — "{Brand} Card" plus a masked "•••• {last4}" when the last4 exists. */
export function cardLabel(card: PaymentCard): string {
  return card.last4 === null ? `${card.brand} Card` : `${card.brand} Card •••• ${card.last4}`;
}

function normalizeMonth(month: string | null | undefined): string | null {
  if (typeof month !== 'string') return null;
  const trimmed = month.trim();
  if (trimmed.length === 0 || !/^\d{1,2}$/.test(trimmed)) return null;
  return trimmed.padStart(2, '0');
}

function normalizeYear(year: string | null | undefined): string | null {
  if (typeof year !== 'string') return null;
  const trimmed = year.trim();
  if (trimmed.length === 0 || !/^\d{2,4}$/.test(trimmed)) return null;
  return trimmed.slice(-2);
}
